// MS-6466: the 60-second session watch. Without it, a superseded browser only learns of the
// takeover when the user next touches the API; with it, the zombie window is bounded at one
// interval — and at ~zero on tab refocus, which is exactly when a takeover should surface.
//
// Endpoint choice is DECIDED (MS-6466): Showroom polls book-api's own same-origin
// /session/check/ (its own service, no extra CORS surface), which book-api answers by asking
// user-api's /usersession/ping — the ONE verdict source both products share. book-api asks the
// ping rather than reading a table because Showroom is single-tenanted (a direct central-table
// read would need cross-account IAM per tenant). Deliberately thin regardless: if the shared
// session client (/_session/v1.js) ever ships, this file and book-api's branch retire together.
import config from '../../config';
import { handleSessionLost, isSessionSuperseded } from './session';

export const SESSION_CHECK_INTERVAL_MS = 60000;

// Each check must finish (or die) well inside one interval: fetch has no built-in timeout, and
// an app-lifetime watcher stacking hung requests against a stalled endpoint is a slow leak.
export const SESSION_CHECK_TIMEOUT_MS = 15000;

// Playwright shortens the interval via addInitScript; a build-time env can't vary per test and
// would need its own webpack DefinePlugin entry in every config.
function intervalMs() {
  // eslint-disable-next-line no-underscore-dangle
  const override = typeof window !== 'undefined' && window.__MHUB_SESSION_CHECK_INTERVAL_MS__;
  return Number(override) > 0 ? Number(override) : SESSION_CHECK_INTERVAL_MS;
}

// One check against book-api's middleware-served endpoint. Acts ONLY on the definitive contract
// pair (401 + body match); network errors, timeouts and 5xx are ambient failures — treating them
// as takeover fabricates a "someone signed in on another device" explanation (the CRM lesson).
export async function checkSessionOnce({ fetcher, onSessionLost } = {}) {
  const base = config.get('bookApiURL');
  if (!base) {
    return; // bookApiURL only resolves after the login bootstrap; nothing to ask yet
  }
  const doFetch = fetcher || ((url, opts) => fetch(url, opts));
  const Aborter = typeof window !== 'undefined' ? window.AbortController : undefined;
  const controller = Aborter ? new Aborter() : null;
  const deadline = controller
    ? setTimeout(() => controller.abort(), SESSION_CHECK_TIMEOUT_MS)
    : null;
  let res;
  try {
    res = await doFetch(`${base}/session/check/`, {
      credentials: 'include',
      signal: controller ? controller.signal : undefined,
    });
  } catch (e) {
    return; // aborted or failed — ambient either way
  } finally {
    if (deadline) {
      clearTimeout(deadline);
    }
  }
  if (res.status !== 401) {
    return;
  }
  let body = '';
  try {
    body = await res.text();
  } catch (e) {
    return; // an unreadable body is not a verdict
  }
  if (isSessionSuperseded(res.status, body)) {
    (onSessionLost || handleSessionLost)();
  }
}

// Mounted once for the app's lifetime (Root.jsx). Ticks on the interval and on tab refocus;
// skips hidden tabs (browsers throttle their timers anyway) and unauthenticated states.
// Returns the stop function.
export function startSessionWatch({
  isAuthenticated, fetcher, onSessionLost, doc, win,
} = {}) {
  const d = doc || document;
  const w = win || window;
  // One check at a time: a hung request must not stack another on the next tick — the abort
  // deadline bounds how long this gate can stay closed.
  let inFlight = false;
  const tick = () => {
    if (inFlight) {
      return;
    }
    if (d.visibilityState === 'hidden') {
      return;
    }
    if (isAuthenticated && !isAuthenticated()) {
      return;
    }
    inFlight = true;
    checkSessionOnce({ fetcher, onSessionLost }).finally(() => {
      inFlight = false;
    });
  };
  const onVisibility = () => {
    if (d.visibilityState === 'visible') {
      tick();
    }
  };
  const timer = w.setInterval(tick, intervalMs());
  d.addEventListener('visibilitychange', onVisibility, false);
  return function stopSessionWatch() {
    w.clearInterval(timer);
    d.removeEventListener('visibilitychange', onVisibility, false);
  };
}



// WEBPACK FOOTER //
// ./src/utils/sessionWatch.js